import type {IPointData} from '@pixi/core';
import {useApp} from '@pixi/react';
import type {DisplayObject, EventMode, FederatedPointerEvent} from 'pixi.js';
import {useCallback, useRef} from 'react';
import {useActiveEditMode, useClearActiveEdit, useIsLinkCreationActive, useStartDrag} from '../state/networkEditStore';

interface UseDraggableProps {
    callback?: (position: IPointData) => void
}

export function useDraggable({callback}: UseDraggableProps) {
    const linkCreationActive = useIsLinkCreationActive();
    const editMode = useActiveEditMode();
    const setStartDragMode = useStartDrag();
    const clearActiveEdit = useClearActiveEdit();
    const isDraggingRef = useRef(false);
    const app = useApp();

    const getScale = (y: number) => (y + 500) / 1080;

    const handlePointerDown = useCallback((event: FederatedPointerEvent) => {
        if (linkCreationActive || editMode === 'addPlayer') return;
        const target = event.currentTarget as DisplayObject;
        console.log('useDraggable - starting drag');
        isDraggingRef.current = true;
        target.alpha = 0.5;
        // enter the exclusive dragging mode
        setStartDragMode();
    }, [editMode, linkCreationActive]);

    const handlePointerMove = useCallback((event: FederatedPointerEvent) => {
        if (linkCreationActive || editMode === 'addPlayer') return;
        if (!isDraggingRef.current) return;
    
        const target = event.currentTarget as DisplayObject;
        if (target.parent) {
            const scale = getScale(event.global.y);
            target.scale = {x: scale, y: scale};
            target.parent.toLocal(event.global, undefined, target.position);
            app.render(); // Don't queue for next MicroTask, render NOW.
        }
    }, [getScale, editMode, linkCreationActive]);

    const handlePointerUp = useCallback((event: FederatedPointerEvent) => {
        if (linkCreationActive || editMode === 'addPlayer') return;
        const target = event.currentTarget as DisplayObject;
        isDraggingRef.current = false;
        target.alpha = 1;
        // leave dragging mode → back to “none”
        clearActiveEdit();
        callback?.(target.position);
    }, [editMode, linkCreationActive, callback]);

    return {
        // Don't capture events if drag is active on a DIFFERENT draggable instance.
        eventMode: ((isDraggingRef.current || editMode !== 'dragNode') ? 'static' : 'none') as EventMode,
        pointerdown: handlePointerDown,
        pointermove: handlePointerMove,
        pointerup: handlePointerUp,
        pointerupoutside: handlePointerUp,
    };
}
